import { type MutateOptions, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { ArrowDownFromLine, Check, X } from 'lucide-react'
import { type ChangeEvent, useContext, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { IconButton } from 'slab'
import { getGetBrokersUnprocessedEnrollmentsIdQueryKey } from 'utils/web_api'
import { type BrokersEnrollment } from 'utils/web_api.schemas'

import { type InsurancePremium } from './types'
import { months, sanitizeNumericInput } from '../../utils'
import BrokersMemberContext from '../context/BrokersMemberContext'
import CoverageAuditReasonContext from '../context/CoverageAuditReasonContext'
import {
  getEnrollmentInsurancePremiumsQueryKey,
  useGetEnrollmentInsurancePremiums,
} from '../hooks/enrollments/useGetEnrollmentInsurancePremiums'
import {
  type InsurancePremiumPatchParam,
  useModifyEnrollmentInsurancePremium,
} from '../hooks/enrollments/useModifyEnrollmentInsurancePremium'
import { Banner } from '../reusable/Banner'
import { CollapsibleDataContainer } from '../reusable/CollapsibleDataContainer'
import LoadingSpinnerCentered from '../reusable/LoadingSpinnerCentered'

// EditEnrollmentInsurancePremiums is a component that allows the user to edit the insurance premiums for an enrollment.
function EditEnrollmentInsurancePremiums ({
  enrollment,
}: {
  enrollment: BrokersEnrollment
}) {
  const { memberId } = useContext(BrokersMemberContext)

  const {
    data: insurancePremiums,
    isLoading,
    isError,
  } = useGetEnrollmentInsurancePremiums({
    enrollmentId: enrollment.id,
    memberId,
  })

  if (isLoading) {
    return (
      <>
        <div className='text-lg font-semibold mb-2'>Insurance Premiums</div>
        <LoadingSpinnerCentered />
      </>
    )
  }

  if (isError) {
    return (
      <Banner className='mb-4' titleText='Something went wrong' type='critical'>
        <p>
          An error occurred while loading insurance premiums. Please contact
          support for assistance.
        </p>
      </Banner>
    )
  }

  const sortedInsurancePremiums =
    insurancePremiums?.sort(
      (a, b) =>
        new Date(a.period_start_date).getTime() -
        new Date(b.period_start_date).getTime()
    ) || []

  const successContent = (
    <div className='p-4'>
      <div className='text-sm font-light'>
        Edit the insurance premium records / losses below. The latest insurance
        premium record determines the price displayed for the user. Only up to
        the next month's premium will be generated at any point.
      </div>
      <div className='flex flex-col justify-start items-start space-y-2 pt-2'>
        <div className='flex justify-between items-center w-full font-semibold'>
          <div className='w-[30%]'>Month</div>
          <div className='w-[70%] flex justify-end items-center'>
            <div className='w-[30%]'>Premium</div>
            <div className='w-[30%]'>Loss</div>
            <div className='w-[40%]' />
          </div>
        </div>

        {sortedInsurancePremiums.map((premium, index) => (
          <EditInsurancePremiumAndLossInput
            key={index}
            enrollment={enrollment}
            premium={premium}
            currentAndFuturePremiums={sortedInsurancePremiums.slice(index)}
          />
        ))}
      </div>
    </div>
  )

  return (
    <CollapsibleDataContainer
      heading='Insurance Premiums'
      isLoading={isLoading}
      error={isError}
      localStorageKey='edit-enrollment-insurance-premiums'
      successContent={successContent}
    />
  )
}

const EditInsurancePremiumAndLossInput = ({
  premium,
  enrollment,
  currentAndFuturePremiums,
}: {
  premium: InsurancePremium
  enrollment: BrokersEnrollment
  currentAndFuturePremiums: InsurancePremium[]
}) => {
  const queryClient = useQueryClient()

  const [premiumValue, setPremiumValue] = useState<string>(
    (premium.premium.amount / 100).toFixed(2)
  )
  const [lossValue, setLossValue] = useState<string>(
    (premium.loss.amount / 100).toFixed(2)
  )
  const { memberId } = useContext(BrokersMemberContext)
  const { reason, openReasonDropdown } = useContext(CoverageAuditReasonContext)
  const month = months[new Date(premium.period_start_date).getUTCMonth()]

  useEffect(() => {
    setPremiumValue((premium.premium.amount / 100).toFixed(2))
    setLossValue((premium.loss.amount / 100).toFixed(2))
  }, [premium.premium.amount, premium.loss.amount])

  const modifyEnrollmentInsurancePremiumMutation =
    useModifyEnrollmentInsurancePremium({
      enrollmentId: enrollment.id,
      memberId,
    })

  const isDisabled =
    (premiumValue === (premium.premium.amount / 100).toFixed(2) &&
    lossValue === (premium.loss.amount / 100).toFixed(2))
  const hasNoReason = !reason

  const getMutationOptions = (
    months: string
  ): MutateOptions<
    InsurancePremium[],
    GrapeAPIErrorType,
    InsurancePremiumPatchParam,
    unknown
  > => ({
    onSuccess: (data) => {
      toast.success(
        `Successfully updated insurance premium and loss for ${months}`,
        {
          position: 'top-center',
        }
      )
      queryClient.setQueryData(
        getEnrollmentInsurancePremiumsQueryKey({
          enrollmentId: enrollment.id,
          memberId,
        }),
        data
      )
      queryClient.invalidateQueries({
        queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(enrollment.id),
      })
    },
  })

  const onUpdateClickHandler = () => {
    if (hasNoReason) {
      openReasonDropdown?.()
      return
    }
    if (!isDisabled) {
      modifyEnrollmentInsurancePremiumMutation.mutate(
        {
          premium_ids: [premium.id],
          premium_amount: premiumValue,
          loss_amount: lossValue,
          member_updated_reason: reason,
        },
        getMutationOptions(month)
      )
    }
  }

  const onUpdateCurrentAndFuturePremiumsClickHandler = () => {
    if (hasNoReason) {
      openReasonDropdown?.()
      return
    }
    if (!isDisabled) {
      modifyEnrollmentInsurancePremiumMutation.mutate(
        {
          premium_ids: currentAndFuturePremiums.map((p) => p.id),
          premium_amount: premiumValue,
          loss_amount: lossValue,
          member_updated_reason: reason,
        },
        getMutationOptions(`${month} through EOY`)
      )
    }
  }

  // Only allow numbers and decimal points as inputs.
  const onPremiumChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setPremiumValue(sanitizeNumericInput(e.target.value))
  }

  const onLossChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setLossValue(sanitizeNumericInput(e.target.value))
  }

  const onCancelHandler = () => {
    setPremiumValue((premium.premium.amount / 100).toFixed(2))
    setLossValue((premium.loss.amount / 100).toFixed(2))
  }

  return (
    <div className='flex justify-between items-center w-full'>
      <div className='w-[30%]'>{month}</div>
      <div className='w-[70%] flex items-center space-x-1 justify-end'>
        <input
          className={clsx({
            'input-default w-[30%]': true,
          })}
          value={premiumValue}
          onChange={onPremiumChangeHandler}
          placeholder='Premium amount'
        />
        <input
          className={clsx({
            'input-default w-[30%]': true,
          })}
          value={lossValue}
          onChange={onLossChangeHandler}
          placeholder='Loss amount'
        />
        <div className='w-[40%] flex justify-start items-center space-x-1'>
          <IconButton
            disabled={isDisabled}
            onClick={onUpdateClickHandler}
            icon={Check}
            label='Confirm changes'
            variant='primary'
            size='large'
            data-testid={`insurance_premiums_save_${premium.id}`}
          />
          <IconButton
            disabled={isDisabled}
            onClick={onCancelHandler}
            icon={X}
            label='Cancel changes'
            variant='secondary'
            size='large'
            data-testid={`insurance_premiums_cancel_${premium.id}`}
          />
          <IconButton
            disabled={isDisabled}
            onClick={onUpdateCurrentAndFuturePremiumsClickHandler}
            icon={ArrowDownFromLine}
            label='Update current and future premiums'
            variant='secondary'
            size='large'
            data-testid={`insurance_premiums_save_current_and_future_${premium.id}`}
          />
        </div>
      </div>
    </div>
  )
}

export default EditEnrollmentInsurancePremiums
